import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, RefreshCw, Search, Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';

const STATUS_CHIP = {
  sent: { icon: CheckCircle2, className: 'bg-[#EDF3EE] text-[#2C5530] border-[#2C5530]/30' },
  failed: { icon: XCircle, className: 'bg-[#FBEAEA] text-[#8B1E1E] border-[#8B1E1E]/30' },
  skipped: { icon: Clock, className: 'bg-[#FAF6EF] text-[#8A6D3B] border-[#8A6D3B]/30' },
  queued: { icon: Clock, className: 'bg-[#F2F0E9] text-[#57534E] border-[#57534E]/30' },
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const AdminEmailLogPage = () => {
  const [state, setState] = useState({ rows: [], summary_30d: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (statusFilter) params.set('status', statusFilter);
      const { data } = await API.get(`/admin/email-log?${params.toString()}`);
      setState(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const summary = state.summary_30d || {};
  const totals = useMemo(() => Object.values(summary).reduce((a, b) => a + b, 0), [summary]);

  return (
    <div className="min-h-screen bg-[#FAFAF9] py-10" data-testid="admin-email-log">
      <div className="container mx-auto px-6 md:px-12 max-w-6xl">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-[#57534E] hover:text-[#1A1A1A] mb-6"><ArrowLeft className="w-4 h-4" /> Admin</Link>

        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <div>
            <p className="text-[#BC562B] text-xs uppercase tracking-[0.35em] mb-2 flex items-center gap-2"><Mail className="w-4 h-4" /> Email log</p>
            <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A]">Outbound emails</h1>
            <p className="text-[#57534E] text-sm mt-1">Every message the platform has sent, newest first.</p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 border border-[#57534E] text-[#57534E] px-4 py-2 text-xs uppercase tracking-wider font-semibold hover:bg-[#F2F0E9] disabled:opacity-50"
            data-testid="email-log-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-white border border-[#E7E5E4] p-4">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#A8A29E]">Last 30d · Total</p>
            <p className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mt-1">{totals}</p>
          </div>
          <div className="bg-white border border-[#E7E5E4] p-4">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#2C5530]">Sent</p>
            <p className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mt-1">{summary.sent || 0}</p>
          </div>
          <div className="bg-white border border-[#E7E5E4] p-4">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#8B1E1E]">Failed</p>
            <p className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mt-1">{summary.failed || 0}</p>
          </div>
          <div className="bg-white border border-[#E7E5E4] p-4">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A6D3B]">Skipped</p>
            <p className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mt-1">{summary.skipped || 0}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#E7E5E4] p-4 mb-6 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="flex-1 flex items-center gap-2 border border-[#E7E5E4] px-3 py-2">
            <Search className="w-4 h-4 text-[#A8A29E]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
              placeholder="Search recipient, subject, or template…"
              className="flex-1 border-0 outline-none text-sm bg-transparent"
              data-testid="email-log-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-[#E7E5E4] px-3 py-2 text-sm bg-white"
            data-testid="email-log-status-filter"
          >
            <option value="">All statuses</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="skipped">Skipped</option>
          </select>
          <button
            onClick={load}
            className="bg-[#1A2E1D] text-white px-5 py-2 text-xs uppercase tracking-wider font-semibold hover:bg-[#0F1F12]"
            data-testid="email-log-apply"
          >
            Apply
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#BC562B] mx-auto" /></div>
        ) : state.rows.length === 0 ? (
          <div className="bg-white border border-[#E7E5E4] p-12 text-center" data-testid="email-log-empty">
            <Mail className="w-8 h-8 text-[#E7E5E4] mx-auto mb-3" />
            <p className="text-[#A8A29E] text-sm">No emails found for the current filters.</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E7E5E4] overflow-x-auto" data-testid="email-log-table">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAF9] text-[#57534E]">
                <tr className="text-xs uppercase tracking-wider">
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Recipient</th>
                  <th className="text-left px-4 py-3">Subject</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">BCC</th>
                </tr>
              </thead>
              <tbody>
                {state.rows.map((r) => {
                  const s = STATUS_CHIP[r.status] || STATUS_CHIP.queued;
                  const Icon = s.icon;
                  return (
                    <tr key={r.id} className="border-t border-[#F2F0E9]" data-testid={`email-log-row-${r.id}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-[#57534E]">
                        <div>{timeAgo(r.created_at)}</div>
                        <div className="text-[10px] text-[#A8A29E]">{new Date(r.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                      </td>
                      <td className="px-4 py-3 text-[#1A1A1A]">
                        <div className="font-semibold">{r.to || '—'}</div>
                        {r.template && <div className="text-[10px] uppercase tracking-wider text-[#A8A29E] mt-0.5">{r.template}</div>}
                      </td>
                      <td className="px-4 py-3 text-[#57534E]">{r.subject}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 border text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 ${s.className}`}>
                          <Icon className="w-3 h-3" /> {r.status}
                        </span>
                        {r.error && <div className="text-[11px] text-[#8B1E1E] mt-1 max-w-xs truncate" title={r.error}>{r.error}</div>}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#57534E]">{(r.bcc && r.bcc.length) ? r.bcc.join(', ') : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmailLogPage;
